import React, { PureComponent } from 'react';
import { StatusType } from '../../api/api.types';
import {
  RatePlanListAllModel, RatePlanModel, RateType
} from '../../api/pricing/pricing.types';
import { getRatePlanTimelineMatrix } from '../../helpers/rateplan.helper';
import { TimelineObject } from './TimelineObject';
import dayjs from 'dayjs';
import { isSameOrBefore } from '../../helpers/date.helper';
import {WithTranslation, withTranslation} from 'react-i18next';

interface RatesTimelineProps extends WithTranslation {
  selectedRatePlan?: RatePlanModel;
  startDate: string;
  endDate: string;
  isOverview?: boolean;
  ratePlansSortedByDesk: any | null;
  onTimelineObjectClick: (id: number) => void;
  cacheUpdatedStarted?: string;
}

interface RatesTimelineState {
  isScrolling: boolean;
  clientX: number;
  scrollX: number;
}

class RatesTimeline extends PureComponent<RatesTimelineProps, RatesTimelineState> {

  readonly state = {
    isScrolling: false,
    clientX: 0,
    scrollX: 0
  };

  ref: any = React.createRef();

  onMouseDown = (e: any) => {
    this.setState({
      isScrolling: true,
      clientX: e.clientX
    });
  };

  onMouseUp = () => {
    this.setState({ isScrolling: false });
  };

  onMouseMove = (e: any) => {
    const { clientX, scrollX } = this.state;
    if (this.state.isScrolling) {
      this.ref.current.scrollLeft = scrollX - e.clientX + clientX;
      this.setState({
        scrollX: scrollX - e.clientX + clientX,
        clientX: e.clientX
      });
    }
  };

  getTableHeaders = () => {
    const headers: any[] = [];
    let date = dayjs(this.props.startDate);
    const endDate = this.props.isOverview ? dayjs(this.props.endDate) : dayjs(this.props.endDate)
        .subtract(1, 'day');
    const currentDate = dayjs().startOf('day');

    if (this.props.isOverview) headers.push(<th className="overview-row-spacer"></th>);

    let prevMonth = date.month() - 1;
    let monthName;
    while (isSameOrBefore(date, endDate)) {

      if (prevMonth !== date.month()) {
        monthName = `${date.format('MMMM').substring(0, 3)} ${date.format('YYYY')}`;
      } else monthName = undefined;
      headers.push((
        <>
          {date.isSame(currentDate) && (
            <div id="currentDateLine" className="current-date-line"></div>
          )}
          <th className={`${this.props.isOverview ? 'timeline-table-column-overview' : 'timeline-table-column'} timeline-table-header`}>
            {monthName && (
              <>
                {!this.props.isOverview && (
                  <div className="grey-vertical-lne"></div>
                )}
                <div className="timeline-month-name mb-1">{monthName}</div>
              </>
            )}
            {this.props.isOverview && (
              <>
                {date.day() === 1 && (
                  <div className="grey-vertical-lne"></div>
                )}
              </>
            )}
          </th>
        </>
      ));

      prevMonth = date.month();
      date = date.add(1, 'day');
    }

    return headers;
  };

  onTimelineObjectSelect = (id: number) => {
    this.props.onTimelineObjectClick(id);
  };

  getTimelineRow = (ratePlans: RatePlanListAllModel[], rowHeader: string, index: number) => {
    let date = dayjs(this.props.startDate);
    const timelineRow: any[] = [];
    if (this.props.isOverview) timelineRow.push(<td><div className="overview-row-spacer" /></td>);

    ratePlans.forEach((rp) => {
      let colSpan = 0;
      let validFrom = dayjs(rp.validFrom);
      if (dayjs(rp.validFrom).isBefore(dayjs(this.props.startDate))) validFrom = dayjs(this.props.startDate);

      while (isSameOrBefore(date, dayjs(rp.validTo)) &&
      isSameOrBefore(date, dayjs(this.props.endDate).subtract(1, 'day'))) {
        if (date.isBefore(validFrom)) {
          let whiteSpace = 0;

          while (date.isBefore(validFrom) && date.isBefore(this.props.endDate)) {
            whiteSpace += 1;
            date = date.add(1, 'day');
          }

          timelineRow.push((<td key={`whitespace ${date.toString()}`} colSpan={whiteSpace}></td>));
        }

        colSpan += 1;
        date = date.add(1, 'day');
      }

      let ratePlanUnpublished = false;
      const cacheUpdatedStarted = this.props.cacheUpdatedStarted as unknown as string;
      if (cacheUpdatedStarted) ratePlanUnpublished = dayjs(rp.modifiedAt).isAfter(dayjs(cacheUpdatedStarted));

      if (colSpan !== 0 && dayjs(rp.validFrom).isBefore(this.props.endDate)) {
        timelineRow.push(
          <TimelineObject
            isFirst={index === 0}
            isOverview={this.props.isOverview}
            ratePlanId={rp.id}
            ratePlanName={rp.name}
            duration={colSpan}
            isAdjustment={rp.rateType === RateType.ADJUSTMENT ? true : undefined}
            isInactive={rp.statusType === StatusType.INACTIVE ? true : undefined}
            isSelected={this.props.selectedRatePlan && this.props.selectedRatePlan.id === rp.id ? true : undefined}
            onClick={(id: number) => this.onTimelineObjectSelect(id)}
            planUnpublished={ratePlanUnpublished}
          />
        );
      }
    });

    timelineRow.push((<td colSpan={1000}></td>));

    const row = (
      <tr>
        <td className={this.getRowClass(index, rowHeader)}>
          <div className={`${this.getRowHeaderClass()}`}>{rowHeader}</div>
        </td>
        {timelineRow}
      </tr>
    );

    return row;
  };

  getRowClass = (index: number, header: string) => {
    let className = header === 'Standard rates' ? 'standard-rate-cell-header' : 'adjustment-cell';

    if (this.props.isOverview) className = className.concat(' timeline-row-header-overview ');
    return className.concat(index === 0 ? ' pt-1' : '');
  };

  getRowHeaderClass = () => (this.props.isOverview ? 'timeline-row-header-overview pl-2 pr-2' : 'timeline-row-header pl-2 pr-2');

  getTimelineForDeskGroup = (deskGroupName: string, ratePlans: RatePlanListAllModel[][], showGroupName?: boolean, showHeaders?: boolean) => {
    const timelineRows: any[] = [];
    const { t } = this.props;

    let rowHeader = ' ';
    let inx = 0;
    ratePlans.forEach((rp, index) => {
      if (rp.length) {
        if (index === (ratePlans.length - 2)) rowHeader = t('l.rate.adjustments');
        else if (index === (ratePlans.length - 1)) rowHeader = t('l.rate.standard_rates');

        timelineRows.push(this.getTimelineRow(rp, rowHeader, inx));
        inx++;
      }
    });

    const timelineForDeskGroup = (
      <>
        <thead>
          <tr>
            {showGroupName && (
              <div className={this.props.isOverview ? 'timeline-table-column-first-overview' : 'timeline-table-column-first mb-1'}>
                <label className="table-header-group-name">{deskGroupName}</label>
              </div>
            )}
            {showHeaders && (
              <>
                {this.getTableHeaders()}
              </>
            )}
          </tr>
        </thead>
        <tbody className="timeline-table-body">
          {timelineRows}
        </tbody>
      </>
    );

    return timelineForDeskGroup;
  };

  getTimelines = (ratePlansSortedByDeskGroup: any) => {
    const timelinesForDeskGroups: any[] = [];
    const keys: string[] = Array.from(ratePlansSortedByDeskGroup.keys());

    keys.forEach((k, index) => {
      const matrixForDeskGroup = getRatePlanTimelineMatrix(ratePlansSortedByDeskGroup.get(k));
      timelinesForDeskGroups.push(this.getTimelineForDeskGroup(k, matrixForDeskGroup, true, index === 0 ? true : undefined));
    });

    return (
      <table className="timeline-table">
        {timelinesForDeskGroups}
      </table>
    );
  };

  render() {
    return (
      <div
        className={`container-fluid ${this.props.isOverview ? 'overview-timeline-background pt-1' : 'scrolling-timeline padding-bottom-20'}`}
        ref={this.ref}
        onMouseDown={(e: any) => this.onMouseDown(e)}
        onMouseUp={this.onMouseUp}
        onMouseMove={(e: any) => this.onMouseMove(e)}
        onMouseLeave={() => this.setState({ isScrolling: false })}
      >
        {this.props.ratePlansSortedByDesk && (this.getTimelines(this.props.ratePlansSortedByDesk))}
      </div>
    );
  }
}

export default withTranslation()(RatesTimeline);
